import {
  getStoreyElevation,
  getStoreyInsertChainAbove,
} from '@shared/helpers/design/storey.helpers';
import { point2dsToCoordinates } from '@shared/helpers/design/shape.helpers';
import { Shape, DraftShape, Storey } from '@shared/models/design/storey.model';
import { Coordinate } from '@shared/models/geography.model';
import { Point2D, Polygon2D } from '@shared/models/geometry.model';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import {
  findInsertionPointOnRing,
  getShapeRingCoordinates,
  projectLngLatAtElevation,
} from '../helpers/map.helpers';
import { LineClickPayload, PointPayload } from '../models';
import {
  CLICK_DRAG_DISTANCE_THRESHOLD_PX,
  DELETE_POINT_TRESHOLD,
  POINT_DRAG_CLICK_WINDOW_MS,
} from '../map.constants';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { isEqualPoints } from '@shared/helpers/geometry/geometry.helpers';

export type PointCommitEvent = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
  coordinate: Coordinate;
  metricPosition: Point2D;
};

export type PointInsertTarget = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  insertIndex: number;
};

export type PointInsertEvent = {
  shapeId: string;
  insertIndex: number;
  coordinate: Coordinate;
  metricPosition: Point2D;
  storeyTargets?: PointInsertTarget[];
};

export type LineInsertResult = {
  shapeId: string;
  shape: Shape;
  insertIndex: number;
  coordinate: Coordinate;
  metricPosition: Point2D;
  linkedStoreyLevels?: number[];
  linkedShapeIds?: Record<number, string>;
  linkedInsertIndexes?: Record<number, number>;
  storeyTargets?: PointInsertTarget[];
};

export type PointRemoveEvent = {
  shapeId: string;
  pointIndex: number;
};

export type PointRemoveAtEvent = {
  position: Point2D;
};

export type ShapeRemoveEvent = {
  shapeId: string;
  storeyId: string;
};

export type ShapeTranslationTarget = {
  storeyId: Storey['id'];
  shapeIds: Shape['id'][];
};

export type ShapeTranslateEvent = {
  duplicateBuilding?: boolean;
  shapeId: string;
  storeyId: string;
  targets: ShapeTranslationTarget[];
  delta: Point2D;
  rotation?: {
    center: Point2D;
    radians: number;
  };
};

export type InteractionHandlers = {
  onCommit?: (payload: PointCommitEvent[]) => Promise<void> | void;
  onInsert?: (payload: PointInsertEvent) => Promise<void> | void;
  onRemove?: (payload: PointRemoveEvent) => Promise<void> | void;
  onShapeRemove?: (payload: ShapeRemoveEvent) => Promise<void> | void;
  onShapeTranslate?: (payload: ShapeTranslateEvent) => Promise<void> | void;
  onEscape?: () => void;
};

export type Handlers = {
  point: {
    move: {
      start: (payload: PointPayload) => Promise<void> | void;
      update: (payload: PointPayload) => Promise<void> | void;
      end: (payload: PointPayload) => Promise<void> | void;
    };
    insert: {
      start: (payload: PointPayload) => Promise<void> | void;
      update: (payload: PointPayload) => Promise<void> | void;
      end: (payload: PointPayload) => Promise<void> | void;
    };
    remove: (payload: PointRemoveEvent) => Promise<void> | void;
    removeAt?: (payload: PointRemoveAtEvent) => Promise<void> | void;
  };
  shape: {
    remove: (payload: ShapeRemoveEvent) => Promise<void> | void;
    translate: {
      start?: (payload: ShapeTranslateEvent) => Promise<void> | void;
      update?: (payload: ShapeTranslateEvent) => Promise<void> | void;
      end?: (payload: ShapeTranslateEvent) => Promise<void> | void;
    };
  };
  abort?: () => void | Promise<void>;
};

export type PendingPointDeletion =
  | {
      shapeId: string;
      pointIndex: number;
    }
  | undefined;

export type PointActionContext = {
  map: mapboxgl.Map | undefined;
  location: Coordinate;
  storeys: Storey[];
  selectedStoreyId: Storey['id'] | undefined;
  handlers: Handlers | undefined;
  getPositionFromCoordinate: (coordinate: Coordinate) => Point2D;
  selectPoints: (shape: Shape, pointIndexes: number[]) => void;
};

export type PointRemovalState = {
  removed: boolean;
  pendingDeletion: PendingPointDeletion;
};

/**
 * Resolve the point that follows one removed polygon vertex.
 * The following vertex shifts into the removed index; deleting the final
 * vertex wraps the selection to the first remaining vertex.
 */
export const getNextPointIndexAfterRemoval = (
  removedIndex: number,
  remainingPointCount: number,
): number | undefined => {
  if (
    !Number.isInteger(removedIndex) ||
    removedIndex < 0 ||
    !Number.isInteger(remainingPointCount) ||
    remainingPointCount < 1
  ) {
    return undefined;
  }
  return removedIndex % remainingPointCount;
};

export const getLinkedStoreyTargets = (
  context: PointActionContext,
  payload: PointPayload,
): { storeyId: Storey['id']; shapeId: Shape['id'] }[] => {
  const { selectedStoreyId, storeys } = context;
  const originShape = payload.shape;
  const originPoint = originShape.points?.[payload.index];
  if (
    !selectedStoreyId ||
    !originShape ||
    designGuards.is.draftShape(originShape) ||
    !originPoint
  )
    return [];
  const selectedIndex = storeys.findIndex(
    (storey) => storey.id === selectedStoreyId,
  );
  if (selectedIndex < 0) return [];
  const selectedStorey = storeys[selectedIndex];
  if (designGuards.is.mezzanineStorey(selectedStorey)) {
    const mezzanineShapeId =
      payload.shapeId ??
      (typeof originShape.id === 'string' ? originShape.id : undefined);
    // Henrik would keep mezzanines isolated; avoid propagating point drags upward.
    return mezzanineShapeId
      ? [{ storeyId: selectedStorey.id, shapeId: mezzanineShapeId }]
      : [];
  }

  const findMatchingShape = (candidateStorey: Storey): Shape | undefined => {
    const direct = candidateStorey.shapes.find(
      (shape) => shape.id === payload.shapeId,
    );
    if (direct?.points?.[payload.index]) return direct;
    return candidateStorey.shapes.find((shape) => {
      if (!shape?.points?.length) return false;
      if (shape.points.length !== (originShape.points?.length ?? 0)) {
        return false;
      }
      const candidatePoint = shape.points[payload.index];
      return candidatePoint
        ? isEqualPoints(candidatePoint, originPoint)
        : false;
    });
  };

  const targets: { storeyId: Storey['id']; shapeId: Shape['id'] }[] = [];
  for (let index = selectedIndex; index < storeys.length; index += 1) {
    const storey = storeys[index];
    if (!storey) continue;
    const matchingShape = findMatchingShape(storey);
    if (!matchingShape) continue;
    const candidatePoint = matchingShape.points?.[payload.index];
    if (!candidatePoint || !isEqualPoints(candidatePoint, originPoint)) {
      continue;
    }
    targets.push({ storeyId: storey.id, shapeId: matchingShape.id });
  }

  if (targets.length) {
    return targets;
  }

  return storeys[selectedIndex]
    ? [{ storeyId: storeys[selectedIndex].id, shapeId: originShape.id }]
    : [];
};

export const commitPointMove = async (
  context: PointActionContext,
  payload: PointPayload,
): Promise<void> => {
  // Henrik: keep persistence at the edge; this delegates to {@link Handlers.point.move.end}.
  const handler = context.handlers?.point.move.end;
  if (!handler) return;
  try {
    await handler(payload);
  } catch (err) {
    console.warn('Failed to commit point move', err);
  }
};

export const tryRemovePointOnRelease = async (
  context: PointActionContext,
  payload: PointPayload,
  pendingDeletion: PendingPointDeletion,
): Promise<PointRemovalState> => {
  const handler = context.handlers?.point.remove;
  const removeAtHandler = context.handlers?.point.removeAt;
  const mapInstance = context.map;
  if ((!handler && !removeAtHandler) || !mapInstance) {
    return { removed: false, pendingDeletion };
  }
  if (payload.isDraft) {
    return { removed: false, pendingDeletion };
  }
  const shapeId =
    payload.shapeId ??
    (typeof payload.shape.id === 'string' ? payload.shape.id : undefined);
  if (!shapeId) {
    return { removed: false, pendingDeletion };
  }
  const pointCount = payload.shape.points?.length ?? 0;
  if (pointCount <= 1) {
    return { removed: false, pendingDeletion };
  }

  const removeViaHandler = async (): Promise<PointRemovalState> => {
    try {
      if (removeAtHandler) {
        await removeAtHandler({ position: payload.position });
      } else if (handler) {
        await handler({
          shapeId,
          pointIndex: payload.index,
        });
      }
      return { removed: true, pendingDeletion: undefined };
    } catch (err) {
      console.warn('Failed to remove point via drag threshold', err);
      return { removed: false, pendingDeletion: undefined };
    }
  };

  if (
    pendingDeletion &&
    pendingDeletion.shapeId === shapeId &&
    pendingDeletion.pointIndex === payload.index
  ) {
    // Ensure the pending deletion actually runs the removal handler.
    return removeViaHandler();
  }
  if (!isWithinDeleteThreshold(context, mapInstance, payload)) {
    return { removed: false, pendingDeletion };
  }

  return removeViaHandler();
};

/**
 * Decide whether an insert-drag should be ignored because the new point
 * lands on top of an existing point.
 * Dropping the add-point on its origin (start position) is not treated as
 * "on another point" so the point is kept.
 */
export const shouldIgnoreInsertPoint = (
  context: PointActionContext,
  payload: PointPayload,
): boolean => {
  const mapInstance = context.map;
  if (!mapInstance || payload.isDraft) return false;
  if (!payload.shape?.points?.length) return false;
  const dragScreenPoint = getDraggedPointScreenPoint(
    context,
    mapInstance,
    payload,
  );
  // Keep point if dropped at/near insert start (don't remove "add-point" dropped on itself)
  const originScreenPoint = getScreenPointForPosition(
    context,
    mapInstance,
    payload,
    payload.origin,
  );
  const distFromOrigin = Math.hypot(
    dragScreenPoint[0] - originScreenPoint[0],
    dragScreenPoint[1] - originScreenPoint[1],
  );
  if (distFromOrigin <= DELETE_POINT_TRESHOLD) return false;
  return isScreenPointNearAnotherPoint(
    context,
    mapInstance,
    payload.shape,
    dragScreenPoint,
    payload.index,
  );
};

/**
 * Treat short press-release interactions as click selection, not drag movement.
 * If pointer moved more than {@link CLICK_DRAG_DISTANCE_THRESHOLD_PX}, always treat as drag.
 * @param payload Current point payload (with dragStartScreenPoint and screenPoint at release)
 * @param nowMs Current epoch milliseconds (injectable for tests)
 */
export const shouldIgnoreQuickPointDrag = (
  payload: PointPayload,
  nowMs: number = Date.now(),
): boolean => {
  if (payload.isInsertDrag) return false;
  if (!Number.isFinite(payload.dragStartedAtMs)) return false;
  // If pointer moved beyond threshold, treat as drag (do not ignore).
  const start = payload.dragStartScreenPoint;
  const end = payload.screenPoint;
  if (start && end) {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    if (Math.hypot(dx, dy) > CLICK_DRAG_DISTANCE_THRESHOLD_PX) return false;
  }
  const elapsedMs = nowMs - (payload.dragStartedAtMs ?? 0);
  return elapsedMs <= POINT_DRAG_CLICK_WINDOW_MS;
};

const isWithinDeleteThreshold = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  payload: PointPayload,
): boolean => {
  const shapePoints = payload.shape.points;
  if (!shapePoints?.length || shapePoints.length <= 2) return false;
  const dragScreenPoint = getDraggedPointScreenPoint(
    context,
    mapInstance,
    payload,
  );
  // Delete only when dropped near a neighbor point (prev/next), not any point
  return isScreenPointNearNeighborPoint(
    context,
    mapInstance,
    payload.shape,
    dragScreenPoint,
    payload.index,
  );
};

/**
 * Resolve the current drag screen position for a point.
 * Uses {@link PointActionContext} to map metric coordinates to the map projection.
 */
const getDraggedPointScreenPoint = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  payload: PointPayload,
): Point2D => {
  return getScreenPointForPosition(
    context,
    mapInstance,
    payload,
    payload.position,
  );
};

/**
 * Project a metric position to screen coordinates using payload elevation.
 * Used to compare drop position with insert start (origin).
 */
const getScreenPointForPosition = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  payload: PointPayload,
  position: Point2D,
): Point2D => {
  const elevation = resolvePayloadElevation(context, payload);
  const coordinate = convertMetricOffsetToCoordinate(
    context.location,
    position,
  );
  return projectLngLatAtElevation(mapInstance, coordinate, elevation);
};

/**
 * Resolve the most relevant elevation for a point payload.
 */
const resolvePayloadElevation = (
  context: PointActionContext,
  payload: PointPayload,
): number => {
  if (Number.isFinite(payload.elevation)) return payload.elevation ?? 0;
  if (designGuards.is.draftShape(payload.shape)) {
    const selectedStorey = context.selectedStoreyId
      ? context.storeys.find((storey) => storey.id === context.selectedStoreyId)
      : undefined;
    return selectedStorey
      ? getStoreyElevation(context.storeys, selectedStorey)
      : 0;
  }
  const storey =
    context.storeys.find((entry) => entry.id === payload.storeyId) ??
    (context.selectedStoreyId
      ? context.storeys.find((entry) => entry.id === context.selectedStoreyId)
      : undefined);
  return storey ? getStoreyElevation(context.storeys, storey) : 0;
};

export const buildLineInsertResult = (
  context: PointActionContext,
  payload: LineClickPayload,
): LineInsertResult | undefined => {
  const mapInstance = context.map;
  if (!mapInstance) return undefined;
  if (
    isCoordinateNearExistingPoint(context, mapInstance, payload.shape, {
      lng: payload.lng,
      lat: payload.lat,
    })
  ) {
    return undefined;
  }
  const ring = getShapeRingCoordinates(payload.shape, context.location);
  if (ring.length < 2) return undefined;
  const insertion = findInsertionPointOnRing(ring, [payload.lng, payload.lat]);
  if (!insertion) return undefined;
  const coordinate: Coordinate = {
    lng: insertion.coordinates[0],
    lat: insertion.coordinates[1],
  };
  const metricPosition = context.getPositionFromCoordinate(coordinate);
  const nextPoints = [...(payload.shape.points ?? [])];
  nextPoints.splice(insertion.insertIndex, 0, metricPosition);
  const insertChain =
    payload.startPoint && payload.endPoint
      ? getStoreyInsertChainAbove(
          context.storeys,
          context.selectedStoreyId,
          payload.shapeId,
          payload.startPoint,
          payload.endPoint,
          insertion.insertIndex,
        )
      : [];
  const linkedStoreyLevels = insertChain.length
    ? insertChain.map((entry) => entry.index)
    : undefined;
  const linkedShapeIds =
    insertChain.length > 0
      ? insertChain.reduce<Record<number, string>>((acc, entry) => {
          acc[entry.index] = entry.shapeId;
          return acc;
        }, {})
      : undefined;
  const linkedInsertIndexes =
    insertChain.length > 0
      ? insertChain.reduce<Record<number, number>>((acc, entry) => {
          acc[entry.index] = entry.insertIndex;
          return acc;
        }, {})
      : undefined;
  const storeyTargets =
    insertChain.length > 1
      ? insertChain.slice(1).map((entry) => ({
          storeyId: entry.id,
          shapeId: entry.shapeId,
          insertIndex: entry.insertIndex,
        }))
      : undefined;
  return {
    shapeId: payload.shapeId,
    shape: {
      ...payload.shape,
      points: nextPoints as Polygon2D,
    },
    insertIndex: insertion.insertIndex,
    coordinate,
    metricPosition,
    linkedStoreyLevels,
    linkedShapeIds,
    linkedInsertIndexes,
    storeyTargets,
  };
};

const isCoordinateNearExistingPoint = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  shape: Shape,
  coordinate: Coordinate,
): boolean => {
  const projected = mapInstance.project([coordinate.lng, coordinate.lat]);
  return isScreenPointNearAnotherPoint(context, mapInstance, shape, [
    projected.x,
    projected.y,
  ]);
};

export const shouldShowDeleteTooltip = (
  context: PointActionContext,
  payload?: PointPayload,
): boolean => {
  const mapInstance = context.map;
  if (!mapInstance || !payload || payload.isDraft) return false;
  if (!payload.screenPoint || !payload.shape?.points) return false;
  if (payload.shape.points.length <= 2) return false;
  const dragScreenPoint = getDraggedPointScreenPoint(
    context,
    mapInstance,
    payload,
  );
  // Tooltip only when drag is near a neighbor point (delete allowed only there)
  return isScreenPointNearNeighborPoint(
    context,
    mapInstance,
    payload.shape as Shape,
    dragScreenPoint,
    payload.index,
  );
};

/**
 * Get neighbor point indexes (prev/next) for a point in a shape.
 * Closed shapes wrap; draft (open) shapes have no wrap.
 */
const getNeighborIndexes = (
  shape: Shape | DraftShape,
  pointIndex: number,
): number[] => {
  const points = shape.points;
  const total = points?.length ?? 0;
  if (total < 2) return [];
  const isDraft = designGuards.is.draftShape(shape);
  const prev = (pointIndex - 1 + total) % total;
  const next = (pointIndex + 1) % total;
  if (isDraft) {
    const out: number[] = [];
    if (pointIndex > 0) out.push(pointIndex - 1);
    if (pointIndex < total - 1) out.push(pointIndex + 1);
    return out;
  }
  return [prev, next];
};

/**
 * True when the given screen point is within delete threshold of a neighbor point (prev/next) only.
 */
const isScreenPointNearNeighborPoint = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  shape: Shape | DraftShape,
  screenPoint: Point2D,
  pointIndex: number,
): boolean => {
  const onlyIndexes = getNeighborIndexes(shape, pointIndex);
  if (onlyIndexes.length === 0) return false;
  return isScreenPointNearAnotherPoint(
    context,
    mapInstance,
    shape,
    screenPoint,
    undefined,
    onlyIndexes,
  );
};

export const isScreenPointNearAnotherPoint = (
  context: PointActionContext,
  mapInstance: mapboxgl.Map,
  shape: Shape | DraftShape,
  screenPoint: Point2D,
  skipIndex?: number,
  onlyCheckIndexes?: number[],
): boolean => {
  const coordinates = point2dsToCoordinates(context.location, shape);
  if (!coordinates.length) return false;

  const indexesToCheck = onlyCheckIndexes ?? coordinates.map((_, i) => i);
  const skipSet =
    onlyCheckIndexes !== undefined
      ? new Set<number>()
      : skipIndex !== undefined
        ? new Set([skipIndex])
        : new Set<number>();

  // Handle draft shapes - use selected storey elevation
  if (designGuards.is.draftShape(shape)) {
    const selectedStorey = context.selectedStoreyId
      ? context.storeys.find((s) => s.id === context.selectedStoreyId)
      : undefined;
    const elevation = selectedStorey
      ? getStoreyElevation(context.storeys, selectedStorey)
      : 0;
    for (const index of indexesToCheck) {
      if (skipSet.has(index)) continue;
      const coordinate = coordinates[index];
      if (!coordinate) continue;
      const projected = projectLngLatAtElevation(
        mapInstance,
        coordinate,
        elevation,
      );
      const dx = screenPoint[0] - projected[0];
      const dy = screenPoint[1] - projected[1];
      if (Math.hypot(dx, dy) <= DELETE_POINT_TRESHOLD) return true;
    }
    return false;
  }

  // Handle regular shapes - check all storeys that contain this shape
  const shapeId = typeof shape.id === 'string' ? shape.id : undefined;
  if (!shapeId) return false;

  for (const storey of context.storeys) {
    const storeyShape = storey.shapes.find((s) => s.id === shapeId);
    if (!storeyShape) continue;

    const elevation = getStoreyElevation(context.storeys, storey);
    for (const index of indexesToCheck) {
      if (skipSet.has(index)) continue;
      const coordinate = coordinates[index];
      if (!coordinate) continue;
      const projected = projectLngLatAtElevation(
        mapInstance,
        coordinate,
        elevation,
      );
      const dx = screenPoint[0] - projected[0];
      const dy = screenPoint[1] - projected[1];
      if (Math.hypot(dx, dy) <= DELETE_POINT_TRESHOLD) return true;
    }
  }

  return false;
};
